import { useState } from 'react';

import { css } from '@emotion/react';
import { ToucanComponents } from '@jointoucan/toucan-design';
import { useTheme } from '@mui/material';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import Layout from '~/components/shared/Layout';
import Login from '~/components/shared/UserAccount/Login';
import LoginSelection from '~/components/shared/UserAccount/LoginSelection';
import Errors from '~/helpers/errors';
import useExternalAuth from '~/hooks/useExternalAuth';
import { useLoggedInUser } from '~/hooks/useLoggedInUser';
import { getTranslations } from '~/lib/next-i18next';

const { Button } = ToucanComponents;

interface LogInProps {
  isEmailLoginVisibleInitially: boolean;
}

const LogIn = ({ isEmailLoginVisibleInitially }: LogInProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const { loggedInAsRegistered } = useLoggedInUser();

  // Perform client-side redirect since middleware check is only based on token containing an email
  if (loggedInAsRegistered) {
    router.push('/dashboard');
  }

  const { signInWithGoogle, signInWithApple } = useExternalAuth();
  const [isEmailLoginVisible, setIsEmailLoginVisible] = useState(isEmailLoginVisibleInitially);

  return (
    <Layout pageError={Errors.SignInPageError}>
      <div
        css={css`
          display: flex;
          justify-content: center;
        `}
      >
        {isEmailLoginVisible ? (
          <Login
            css={css`
              max-width: 384px;
              padding-top: ${theme.spacing(5)};
              padding-bottom: ${theme.spacing(5)};
              ${theme.breakpoints.up('sm')} {
                padding-top: ${theme.spacing(10)};
                padding-bottom: ${theme.spacing(10)};
              }
            `}
            onLoginComplete={() => {
              router.push('/dashboard');
            }}
            onClickBack={() => {
              setIsEmailLoginVisible(false);
            }}
            onClickForgotPassword={() => {
              router.push('/forgot-password');
            }}
          />
        ) : (
          <LoginSelection
            css={css`
              max-width: 384px;
              padding-top: ${theme.spacing(5)};
              padding-bottom: ${theme.spacing(5)};
              ${theme.breakpoints.up('sm')} {
                padding-top: ${theme.spacing(10)};
                padding-bottom: ${theme.spacing(10)};
              }
            `}
            onClickLoginWithGoogle={() => {
              signInWithGoogle({
                pagePath: '/dashboard',
              });
            }}
            onClickLoginWithApple={() => {
              signInWithApple({
                pagePath: '/dashboard',
              });
            }}
            onClickSignup={() => {
              router.push('/sign-up');
            }}
            loginWithEmailButton={
              <Button
                type="secondary"
                size="medium"
                onClick={() => {
                  setIsEmailLoginVisible(true);
                }}
                css={css`
                  width: 100%;
                  margin-bottom: ${theme.spacing(2)};
                `}
              >
                {t('sharedLogInWithEmail')}
              </Button>
            }
          />
        )}
      </div>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ locale, query }) => {
  return {
    props: {
      ...(await getTranslations({ locale })),
      isEmailLoginVisibleInitially: query.method === 'email',
    },
  };
};

export default LogIn;
